import { h, Fragment } from "preact";
import Router, { RoutableProps } from "preact-router";
import { useEffect, useMemo, useRef, useState } from "preact/hooks";
import { resolvePath } from "../../../api";
import { generate, getLabelIds, labelPreviewUrl, labelsMetadata } from "../../../api/label-creator";
import { createLabel, editLabel, getLabel, getLabels, getProducts, Label, Product } from "../../../api/product-api";
import { baseUrl, route } from "../../../App";
import { Snackbar, SnackbarShowCommand } from "../../../components/Snackbar";
import { parseTapeType } from "../../../components/TemplatePrint";
import { useCommand } from "../../../hooks";
import { generateExampleFieldsForLabel, getExampleSerialNumber, parseToDateAndFormatIfExists } from "../../../utils";
import { predefinedMetadataKeys, TemplateMetadataDisplay } from "../../../components/TemplateMetadataDisplay";
import "./styles.scss";
import { LabelMetadataEditorHelp } from "../LabelMetadataEditorHelp";
import { LabelMetadataFieldEdit } from "../LabelMetadataFieldEdit";
import { LabelSVGInteractive } from "../LabelSVGInteractive";

const predefinedFields = [
    {
        id: "field-date",
        type: "date",
        description: "Data produkcji"
    }, {
        id: "field-serial",
        type: "",
        description: "Numer seryjny"
    }
];

function FieldEdit(props: { metadata: { readonly [key: string]: string }, onChange: (property: string, newValue: string | null) => void }) {
    const [newFieldName, setNewFieldName] = useState("");
    const [newPredefinedFieldName, setNewPredefinedFieldName] = useState("");
    const availablePredefinedFields = predefinedFields.filter(v => !Object.keys(props.metadata).includes(v.id));

        useMemo(() => {
            setNewPredefinedFieldName(availablePredefinedFields[0]?.id ?? "");
        }, [availablePredefinedFields.length])

    return <>
        <div style="margin-top: 20px;">
            <h2><span class="tooltip tooltip-right" data-tooltip="Pola możliwe do wypełnienia przez użytkownika lub system">Pola</span></h2>
            <div>
                <select
                    value={newPredefinedFieldName}
                    disabled={!availablePredefinedFields.length}
                    style={{ pointerEvents: availablePredefinedFields.length ? "" : "none" }}
                    onChange={(e) => setNewPredefinedFieldName(e.currentTarget.value)}
                >
                    {availablePredefinedFields.map(v =>
                        <option value={v.id}>{v.description}</option>
                    )}
                </select>
                <button style="margin-left: 10px;" onClick={() => {
                    const predefinedField = predefinedFields.find(v => v.id === newPredefinedFieldName);

                    if (predefinedField) {
                        props.onChange(newPredefinedFieldName, predefinedField.type);
                        props.onChange("description-" + newPredefinedFieldName, predefinedField.description);
                    }
                }}>
                    Dodaj predefiniowane pole
                </button>
            </div>
            <div style="margin-top: 10px">
                <input type="text" placeholder="Nazwa pola..." value={newFieldName} onChange={(e) => setNewFieldName(e.currentTarget.value)} />
                <button class="secondary" style="margin-left: 10px;" onClick={() => props.onChange("field-" + newFieldName, "")}>
                    Dodaj własne pole
                </button>
            </div>
        </div>
        {Object.keys(props.metadata).filter(v => v.startsWith("field-")).map(v => <div style="margin-top: 10px; display: flex; align-items: center;">
            <label>
                {props.metadata["description-" + v] ? `${props.metadata["description-" + v]} (Pole '${v.substring(6)}')` : `Pole '${v.substring(6)}'`}:
                <select style="margin-left: 5px" type="text" value={props.metadata[v] ?? ""} onChange={(e) => {
                    props.onChange(v, e.currentTarget.value);
                }}>
                    {predefinedMetadataKeys["field-"].autofill!.map(v => <option value={v}>{v}</option>)}
                </select>
                <input style="margin-left: 10px" placeholder="Opis..." value={props.metadata["description-" + v] ?? ""} onChange={(e) => {
                    props.onChange("description-" + v, e.currentTarget.value);
                }}/>
            </label>
            <button class="icon flat error" style="margin-left: 5px; width: 24px; height: 24px; font-size: 18px" onClick={() => {
                props.onChange(v, null);
            }}>delete</button>
        </div>)}
    </>;
}


export function LabelEditOldPage(props: RoutableProps & { id?: string }) {
    const [label, setLabel] = useState(null as null | Label);
    const [labelIds, setLabelIds] = useState([] as string[]);
    const [labelMetadata, setLabelMetadata] = useState({} as { [key: string]: string });
    const [ordersSearch, setOrdersSearch] = useState("");
    const [page, setPage] = useState(0);
    const [count, setCount] = useState(100);
    const [newFieldName, setNewFieldName] = useState("");
    const [newElementName, setNewElementName] = useState("");
    const [newEntryName, setNewEntryName] = useState("");
    const [previewVisible, setPreviewVisible] = useState(true);
    const [labelElementHighlight, setLabelElementHighlight] = useState("");
    const [labelSvg, setLabelSvg] = useState("");
    const showSnackbarCommand = useCommand<SnackbarShowCommand>();
    const templateMetadataDisplayClear = useCommand();
    const templateMetadataDisplaySetMetadata = useCommand<[metadata: { [key: string]: string }]>();

    useEffect(() => {
        if (props.id) {
            getLabel(+props.id).then(async (label) => {
                setLabel(label);

                fetch(labelPreviewUrl(label.path)).then(v => v.text().then(t => setLabelSvg(t)));
            });
        }
        else {
            setLabel({} as Label);
        }
    }, [page, count]);

    useEffect(() => {
        if (!label?.path) {
            setLabelIds([]);
            setLabelMetadata({});
            return;
        }

        getLabelIds(label.path).then(async (label) => {
            setLabelIds(label);
        }).catch(() => {
            showSnackbarCommand.run("Nie udało się pobrać danych naklejki", "error", "error");
            setLabelIds([]);
        });

        labelsMetadata(label.path).then(async (label) => {
            setLabelMetadata(label);
        }).catch(() => {
            showSnackbarCommand.run("Nie udało się pobrać danych naklejki", "error", "error");
            setLabelIds([])
        });
    }, [label?.path])

    if (!label) {
        return <div style="display: flex; flex-direction: column; justify-content: center; align-items: center; width: 100%; height: 100%">
            <div>Pobieranie danych...</div>
            <div class="loader"></div>
        </div>;
    }

    return <div class="label-edit-page">
        <Snackbar doShow={showSnackbarCommand} />
        <div class="order-items paper" style="margin-top: 10px;">
            <h1>{label.id ? "Naklejka: " + label.name : "Nowa naklejka"}</h1>
            <div>
                <label>Nazwa naklejki: <input style="margin-left: 5px" type="text" value={label.name} onChange={(e) => {
                    setLabel({
                        ...label,
                        name: e.currentTarget.value
                    });
                }} /></label>
            </div>
            <div style="margin-top: 10px;">
                <label>Ścieżka: <input style="margin-left: 5px" type="text" size={100} value={label.path} onChange={(e) => {
                    setLabel({
                        ...label,
                        path: e.currentTarget.value.trim()
                    });
                }} /></label>
            </div>
            <div style="margin-top: 10px;">
                <label>Rewizja: <input style="margin-left: 5px" type="text" value={label.revision} onChange={(e) => {
                    setLabel({
                        ...label,
                        revision: e.currentTarget.value
                    });
                }} /></label>
            </div>
            <div style="margin-top: 10px;">
                <label>
                    Drukarka: <select style="margin-left: 5px" type="text" value={label.metadata?.metadata["printer"] ?? ""} onChange={(e) => {
                        setLabel({
                            ...label,
                            metadata: {
                                ...label.metadata,
                                metadata: {
                                    ...label.metadata?.metadata,
                                    "printer": e.currentTarget.value
                                }
                            }
                        });
                    }}>
                        {predefinedMetadataKeys["printer"].values!.map(v => <option value={v}>{v}</option>)}
                    </select>
                </label>
            </div>
            <div style="margin-top: 10px;">
                <label>
                    Szerokość taśmy: <select style="margin-left: 5px" type="text" value={label.metadata?.metadata["tape-width"] ?? ""} onChange={(e) => {
                        setLabel({
                            ...label,
                            metadata: {
                                ...label.metadata,
                                metadata: {
                                    ...label.metadata?.metadata,
                                    "tape-width": e.currentTarget.value
                                }
                            }
                        });
                    }}>
                        {predefinedMetadataKeys["tape-width"].values!.map(v => <option value={v}>{v}</option>)}
                    </select>
                </label>
            </div>
            <div style="margin-top: 10px;">
                <label>
                    Rodzaj taśmy: <select style="margin-left: 5px" type="text" value={label.metadata?.metadata["tape-type"] ?? ""} onChange={(e) => {
                        setLabel({
                            ...label,
                            metadata: {
                                ...label.metadata,
                                metadata: {
                                    ...label.metadata?.metadata,
                                    "tape-type": e.currentTarget.value
                                }
                            }
                        });
                    }}>
                        {predefinedMetadataKeys["tape-type"].values!.map(v => <option value={v}>{v}</option>)}
                    </select>
                </label>
            </div>
            <div style="margin-top: 10px;">
                <label>
                    Wysoka jakość: <select style="margin-left: 5px" type="text" value={label.metadata?.metadata["high-quality"] ?? ""} onChange={(e) => {
                        setLabel({
                            ...label,
                            metadata: {
                                ...label.metadata,
                                metadata: {
                                    ...label.metadata?.metadata,
                                    "high-quality": e.currentTarget.value
                                }
                            }
                        });
                    }}>
                        {predefinedMetadataKeys["high-quality"].values!.map(v => <option value={v}>{v}</option>)}
                    </select>
                </label>
            </div>
            <FieldEdit metadata={label.metadata?.metadata ?? {}} onChange={(prop, value) => {
                if (value !== null) {
                    setLabel(prev => prev ? {
                        ...prev,
                        metadata: {
                            ...prev.metadata,
                            metadata: {
                                ...prev.metadata?.metadata,
                                [prop]: value
                            }
                        }
                    } : prev);
                }
                else {
                    const newLabel = {
                        ...label,
                        metadata: {
                            ...label.metadata,
                            metadata: {
                                ...label.metadata?.metadata,
                            }
                        }
                    };

                    delete newLabel.metadata.metadata[prop];

                    setLabel(newLabel);
                }
            }}/>
            <div style="margin-top: 20px;">
                <h2>Elementy</h2>
                <div style="margin-bottom: 10px;">
                    <LabelSVGInteractive svg={labelSvg} highlight={labelElementHighlight}/>
                </div>
                <select type="text" placeholder="Nazwa elementu..." value={newElementName} onChange={(e) => setNewElementName(e.currentTarget.value)}>
                    {labelIds
                        .filter(v => v.indexOf("-") < 0 && Object.keys(label.metadata?.metadata ?? {})
                            .filter(v => v.startsWith("element-"))
                            .map(v => v.split("-").slice(2)).join("-")
                            .indexOf(v) < 0
                        ).map(v => <option value={v}>{v}</option>)}
                </select>
                <button style="margin-left: 10px;" onClick={() => {
                    if (!newElementName) {
                        return;
                    }

                    setLabel({
                        ...label,
                        metadata: {
                            ...label.metadata,
                            metadata: {
                                ...label.metadata?.metadata,
                                ["element-text-" + newElementName]: ""
                            }
                        }
                    });
                }}>Dodaj element</button>
            </div>
            {Object.keys(label.metadata?.metadata ?? {}).filter(v => v.startsWith("element-")).map(v =>
                <div style="margin-top: 10px; display: flex; align-items: center;"
                    onMouseEnter={() => setLabelElementHighlight(v.split("-").slice(2).join("-"))}
                    onMouseLeave={() => setLabelElementHighlight("")}
                >
                    <label>
                        <span
                            class={labelIds.indexOf(v.split("-").slice(2).join("-")) < 0 ? "tooltip tooltip-error" : ""}
                            style={{
                                backgroundColor: labelIds.indexOf(v.split("-").slice(2).join("-")) < 0 ? "var(--palette-error-dark)" : ""
                            }}
                            data-tooltip="Element o tej nazwie nie wsytępuje w pliku SVG"
                        >
                            Element '{v.split("-").slice(2).join("-")}':
                        </span>
                        <select style="margin-left: 5px" type="text" value={v.split("-")[1]} onChange={(e) => {
                            const elementInfo = v.split("-");
                            const type = e.currentTarget.value;
                            const name = elementInfo.slice(2).join("-");

                            const newLabel = {
                                ...label,
                                metadata: {
                                    ...label.metadata,
                                    metadata: {
                                        ...label.metadata?.metadata,
                                        ["element-" + type + "-" + name]: label.metadata?.metadata[v] ?? ""
                                    }
                                }
                            };

                            delete newLabel.metadata.metadata[v];
                            setLabel(newLabel);
                        }}>
                            {["text", "datamatrix", "qrcode"].map(v => <option value={v}>{v}</option>)}
                        </select>
                        <LabelMetadataFieldEdit metadata={label.metadata?.metadata ?? {}} property={v} onChange={(value) => {
                            setLabel({
                                ...label,
                                metadata: {
                                    ...label.metadata,
                                    metadata: {
                                        ...label.metadata?.metadata,
                                        [v]: value
                                    }
                                }
                            });
                        }}/>
                    </label>
                    <button class="icon flat error" style="margin-left: 5px; width: 24px; height: 24px; font-size: 18px" onClick={() => {
                        const newLabel = {
                            ...label,
                            metadata: {
                                ...label.metadata,
                                metadata: {
                                    ...label.metadata?.metadata,
                                }
                            }
                        };

                        delete newLabel.metadata.metadata[v];

                        setLabel(newLabel);
                    }}>delete</button>
                </div>
            )}
            <div style="margin-top: 20px;">
                <h2>Dodatkowe wpisy</h2>
                <input type="text" placeholder="Klucz wpisu..." value={newEntryName} onChange={(e) => setNewEntryName(e.currentTarget.value)} />
                <button style="margin-left: 10px;" onClick={() => {
                    if (!newEntryName) {
                        return;
                    }

                    setLabel({
                        ...label,
                        metadata: {
                            ...label.metadata,
                            metadata: {
                                ...label.metadata?.metadata,
                                [newEntryName]: ""
                            }
                        }
                    });
                }}>Dodaj wpis</button>
            </div>
            {Object.keys(label.metadata?.metadata ?? {})
                .filter(v => !v.startsWith("field-") && !v.startsWith("element-")  && !v.startsWith("description-"))
                .filter(v => Object.keys(predefinedMetadataKeys).indexOf(v) < 0)
                .map(v => <div style="margin-top: 10px; display: flex; align-items: center;">
                    <label>
                        Wpis '{v}': <LabelMetadataFieldEdit metadata={label.metadata?.metadata ?? {}} property={v} onChange={(value) => {
                            setLabel({
                                ...label,
                                metadata: {
                                    ...label.metadata,
                                    metadata: {
                                        ...label.metadata?.metadata,
                                        [v]: value
                                    }
                                }
                            });
                        }}/>
                    </label>
                    <button class="icon flat error" style="margin-left: 5px; width: 24px; height: 24px; font-size: 18px" onClick={() => {
                        const newLabel = {
                            ...label,
                            metadata: {
                                ...label.metadata,
                                metadata: {
                                    ...label.metadata?.metadata,
                                }
                            }
                        };

                        delete newLabel.metadata.metadata[v];

                        setLabel(newLabel);
                    }}>delete</button>
                </div>)}
            <div style="margin-top: 10px; padding:10px; margin-left: 10px; margin-right: 10px;" class="paper-2">
                <h1>Metadane</h1>
                <div>
                    <TemplateMetadataDisplay
                        template={label.metadata}
                        loadFromStorage={false}
                        onChange={(metadata) => { setLabel({ ...label, metadata: { ...label.metadata, metadata } }); }}
                        doClear={templateMetadataDisplayClear}
                        doSetMetadata={templateMetadataDisplaySetMetadata} />
                </div>
                <div style="margin-top: 10px; display: flex; justify-content: flex-end">
                    <button type="button" class="info" onClick={async () => {
                        await navigator.clipboard.writeText(Object.entries(label.metadata?.metadata ?? {}).map(([key, value]) => `${key}=${value}`).join("\n"));
                        showSnackbarCommand.run("Skopiowano", "info", "info", 2000);
                    }}>
                        <span class="material-icons" style="font-size: 17px; margin-right: 5px">content_copy</span>
                        Kopiuj
                    </button>
                    <button type="button" style="margin-left: 10px;" class="error" onClick={() => templateMetadataDisplayClear.run()}>Wyczyść</button>
                </div>
                <div style={{
                    position: "fixed",
                    bottom: 0,
                    left: 0,
                    right: 0,
                    zIndex: 100,
                    display: "flex",
                    direction: "rtl",
                    pointerEvents: "none"
                }}>
                    <div style={{
                        pointerEvents: "all",
                        resize: "horizontal",
                        padding: "10px 10px",
                        background: "#3f3f3f",
                        overflow: "hidden",
                        overflowY: "auto",
                        maxWidth: "600px",
                        maxHeight: "350px",
                        borderTop: "1px solid #5f5f5f",
                        borderRight: "1px solid #5f5f5f",
                        boxShadow: "#00000047 0px 0px 10px 2px"
                    }}>
                        <img style="width: 100%" src={generate(label.path, generateExampleFieldsForLabel({ ...labelMetadata, ...label.metadata?.metadata }), label.metadata?.metadata)} />
                    </div>
                </div>
                <h2>Podgląd taśmy</h2>
                <div><img style="max-width: 200px" src={`${resolvePath("images/tapes/")}${parseTapeType(label.metadata?.metadata["tape-width"], label.metadata?.metadata["tape-type"] ?? "")}`} /></div>
                <LabelMetadataEditorHelp/>
            </div>
            {label.id ? <div style="margin-top: 10px;">
                <span class={(label.productTypeRevisionLabels ?? []).length > 0 ? "tooltip" : ""} data-tooltip="Nie można edydotwać naklejki, która została przypisana do rewizji produktu">
                    <button type="button" class={(label.productTypeRevisionLabels ?? []).length > 0 ? "disabled" : ""} onClick={async () => {
                        try {
                            await editLabel(label);
                        }
                        catch (ex) {
                            console.error(ex);
                            showSnackbarCommand.run("Wystąpił błąd podczas edytowania naklejki", "error", "error");
                        }

                        route("/label", true);
                    }}>Zastosuj</button>
                </span>
            </div> : <div style="margin-top: 10px;">
                <button type="button" onClick={async () => {
                    try {
                        await createLabel(label);
                    }
                    catch (ex) {
                        console.error(ex);
                        showSnackbarCommand.run("Wystąpił błąd podczas edytowania naklejki", "error", "error");
                    }

                    route("/label", true);
                }}>Utwórz naklejkę</button>
            </div>}
        </div>
        {label ? <div class="paper" style="padding: 10px; margin: 0px 10px; margin-bottom: 10px;">
            <h1>Rewizje produktów do których przypisana jest naklejka</h1>
            <div>
                <table class="list selectable">
                    <thead>
                        <th>Nazwa</th>
                        <th>Brand</th>
                        <th>Wersja</th>
                        <th>Rejestrujący</th>
                        <th>Data rejestracji</th>
                    </thead>
                    <tbody>
                        {label.productTypeRevisionLabels?.map(v => <tr class="item" onClick={() => route("/product-type-revision/" + v.productTypeRevision.id)}>
                            <td>{v.productTypeRevision.productType!.name}</td>
                            <td>{v.productTypeRevision.productType!.brand}</td>
                            <td>{v.productTypeRevision.name}</td>
                            <td>{v.productTypeRevision.creator.email}</td>
                            <td>{parseToDateAndFormatIfExists(v.productTypeRevision.createdAt)}</td>
                        </tr>)}
                    </tbody>
                </table>
            </div>
        </div> : null}
        {previewVisible ? <div style="height: 200px" /> : null}
    </div>;
}
